import React from "react";
import {
  Grid2 as Grid,
  CircularProgress,
  Typography,
  Box,
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { FormProvider } from "react-hook-form";
import useFormHandler, {
  ConnectionFormDataController,
  FormHandlerContext,
} from "../../controllers/formController";
import FormActions from "./common/base/FormActions";
import FormFooter from "./common/base/FormFooter";
import FormConnectionHeader from "./common/base/FormConnectionHeader";
import {
  EntitySelectionProps,
  NodeEntity,
} from "../entitiesReferences/EntitySelectionInput";
import { SystemEntityCollections } from "../../shared/enums/shared-enums";
import { BaseConnection } from "../../types/connection";
import { BaseNodeEntity } from "../../types/baseNodeEntity";

interface ConnectionFormDialogProps<
  TConnection extends BaseConnection,
  TSource extends BaseNodeEntity & NodeEntity,
  TDestination extends BaseNodeEntity & NodeEntity,
> {
  fromLabel: string;
  fromEntityCollection: SystemEntityCollections;
  toLabel: string;
  toEntityCollection: SystemEntityCollections;
  renderFromEntitySelection: (
    props: EntitySelectionProps<TSource>
  ) => React.ReactElement;
  renderToEntitySelection: (
    props: EntitySelectionProps<TDestination>
  ) => React.ReactElement;
  controller: ConnectionFormDataController<TConnection, TSource, TDestination>;
  children?: React.ReactNode;
}

const ConnectionFormDialog = <
  TConnection extends BaseConnection,
  TSource extends BaseNodeEntity & NodeEntity,
  TDestination extends BaseNodeEntity & NodeEntity,
>({
  fromLabel,
  fromEntityCollection,
  toLabel,
  toEntityCollection,
  renderFromEntitySelection,
  renderToEntitySelection,
  controller,
  children,
}: ConnectionFormDialogProps<TConnection, TSource, TDestination>) => {
  const formHandler = useFormHandler<TConnection>(controller);
  const { methods, title, loading, onSubmit, fetchError } = formHandler;
  const { direction, isDialogShown, closeDialog } = controller;

  return (
    <Dialog
      scroll="paper"
      open={isDialogShown}
      onClose={closeDialog}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Box>
          <Typography variant="h2">{title}</Typography>
        </Box>
      </DialogTitle>
      {loading ? (
        <DialogContent dividers>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      ) : fetchError ? (
        <DialogContent dividers>
          <Alert severity="error">{fetchError}</Alert>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button variant="contained" onClick={closeDialog}>
              Close
            </Button>
          </Box>
        </DialogContent>
      ) : (
        <FormHandlerContext.Provider value={formHandler}>
          <FormProvider {...methods}>
            <form onSubmit={onSubmit} noValidate>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <FormConnectionHeader
                    direction={direction}
                    fromLabel={fromLabel}
                    fromEntityCollection={fromEntityCollection}
                    toLabel={toLabel}
                    toEntityCollection={toEntityCollection}
                    renderFromEntitySelection={renderFromEntitySelection}
                    renderToEntitySelection={renderToEntitySelection}
                  />
                  {children}
                  <FormFooter />
                </Grid>
              </DialogContent>
              <DialogActions>
                <FormActions />
              </DialogActions>
            </form>
          </FormProvider>
        </FormHandlerContext.Provider>
      )}
    </Dialog>
  );
};

export default ConnectionFormDialog;
